import { computed } from 'vue'
import type { Ref } from 'vue'
import { useUserStore } from '@/modules/user/stores/userStore'
import { debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import {
  fetchMatrixRoomMembers,
  resolveMatrixRoomIdByChatId,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { resolveEmployeeIdByMatrixUserId } from '@/modules/chat-matrix-fast/services/employeeLookup'
import type { IChat } from '@/modules/chat-matrix-fast/types/IChat'

interface ActiveChat {
  chat: {
    id: number
    type?: string
    members?: any[]
  }
}

interface CoreChat {
  id: number
  members?: any[]
  matrix_room_id?: string
  matrix_peer_id?: string
}

export const useChatEmployee = (
  activeChat: Ref<ActiveChat | null>,
  currentChat: Ref<CoreChat | null>,
  coreChats: Ref<CoreChat[]>,
) => {
  const userStore = useUserStore()

  const findEmployeeUuid = (
    chat: any,
    chatId: number,
    currentUserId: string,
  ): string | null => {
    if (!chat) return null

    if ((chat as any).dialog_member?.id) {
      return (chat as any).dialog_member.id
    }

    if (chat.members && Array.isArray(chat.members)) {
      const dialogMember = chat.members.find((m: any) => {
        const memberId = m.user_uuid || m.user || m.user_id
        return memberId && String(memberId) !== String(currentUserId)
      })

      if (dialogMember) {
        return dialogMember.user_uuid || String(dialogMember.user || dialogMember.user_id)
      }
    }

    return null
  }

  const getEmployeeUuid = computed((): string | null => {
    if (!activeChat.value?.chat) return null

    const chat = activeChat.value.chat
    const chatId = chat.id
    const currentUserId = userStore.user?.uuid || userStore.user?.id?.toString() || ''

    if (currentChat.value && currentChat.value.id === chatId) {
      const uuid = findEmployeeUuid(currentChat.value, chatId, currentUserId)
      if (uuid) return uuid
    }

    const chatFromList = coreChats.value.find(c => c.id === chatId)
    if (chatFromList) {
      const uuid = findEmployeeUuid(chatFromList, chatId, currentUserId)
      if (uuid) return uuid
    }

    const uuid = findEmployeeUuid(chat, chatId, currentUserId)
    if (uuid) return uuid

    if ((activeChat.value as any).contact?.id) {
      const contactId = (activeChat.value as any).contact.id
      const isUuid = typeof contactId === 'string'
        && (contactId.includes('-') || contactId.length > 10)
      if (isUuid) return contactId
    }

    return null
  })

  const toEmployeeUuid = async (id: string | null): Promise<string | null> => {
    if (!id) return null
    if (!id.startsWith('@')) return id
    return (await resolveEmployeeIdByMatrixUserId(id)) ?? null
  }

  const fetchEmployeeUuid = async (): Promise<string | null> => {
    const resolved = await toEmployeeUuid(getEmployeeUuid.value)
    if (resolved) return resolved

    if (!activeChat.value?.chat) return null

    const chat = activeChat.value.chat
    const chatId = chat.id
    const chatType = chat.type

    if (chatType !== 'dialog' && chatType !== 'direct') return null

    const matrixChat = (currentChat.value ?? chat) as IChat
    const peerMxid = matrixChat.matrix_peer_id?.trim()
    if (peerMxid?.startsWith('@')) {
      const fromPeer = await resolveEmployeeIdByMatrixUserId(peerMxid)
      if (fromPeer) return fromPeer
    }

    const roomId = matrixChat.matrix_room_id ?? await resolveMatrixRoomIdByChatId(chatId)

    if (roomId) {
      try {
        const members = await fetchMatrixRoomMembers(roomId)
        const currentUserId = userStore.user?.uuid || userStore.user?.id?.toString()
        const myMxid = (matrixChat as IChat & { matrix_user_id?: string }).matrix_user_id

        const dialogMember = members.find(member => {
          const mxid = member.user_uuid
          if (mxid?.startsWith('@')) {
            return mxid !== myMxid
          }
          const memberId = member.user?.id || member.user_uuid || member.user
          return memberId && String(memberId) !== String(currentUserId)
        })

        if (dialogMember?.user_uuid?.startsWith('@')) {
          return (await resolveEmployeeIdByMatrixUserId(dialogMember.user_uuid)) ?? null
        }
      } catch (error) {
        debugConsoleError('Ошибка при загрузке участников Matrix-комнаты:', error)
      }
    }

    return null
  }

  return {
    getEmployeeUuid,
    fetchEmployeeUuid,
  }
}
